import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth, type User } from '@/context/AuthContext';
import { Users, Scissors, Check, Loader2, AlertCircle } from 'lucide-react';
import { apiUrl } from '@/config/api';
import InternationalPhoneInput from './common/InternationalPhoneInput';
import Navigation from './Navigation';

interface OAuthCompletionData {
  role: 'customer' | 'tailor' | null;
  fullName: string;
  phone: string;
  businessName?: string;
  businessType?: 'tailor' | 'fashion_house' | 'boutique';
}

const OAuthCompletionPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState<OAuthCompletionData>({
    role: null,
    fullName: user?.name || '',
    phone: user?.phone || '',
    businessName: '',
    businessType: 'tailor'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Redirect if already authenticated with a role
  useEffect(() => {
    if (user && user.role && user.role !== 'pending') {
      const destination = 
        user.role === 'customer' ? '/customer/dashboard' :
        user.role === 'tailor' ? '/business/dashboard' :
        user.role === 'admin' ? '/admin/dashboard' : '/customer/dashboard';
      navigate(destination);
    }
  }, [user, navigate]);

  const handleInputChange = (field: keyof OAuthCompletionData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.role) {
      errors.role = 'Please select an account type';
    }

    if (!formData.fullName || formData.fullName.trim().length < 2) {
      errors.fullName = 'Name must be at least 2 characters';
    }

    if (!formData.phone || formData.phone.trim().length < 10) {
      errors.phone = 'Please enter a valid phone number';
    }

    if (formData.role === 'tailor' && !formData.businessName?.trim()) {
      errors.businessName = 'Business name is required for tailors';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token not found. Please try logging in again.');
      }

      const requestData: any = {
        role: formData.role,
        full_name: formData.fullName.trim(),
        phone: formData.phone.trim()
      };

      if (formData.role === 'tailor') {
        requestData.business_name = formData.businessName?.trim();
        requestData.business_type = formData.businessType || 'tailor';
      }

      const response = await fetch(apiUrl('/api/auth/complete-oauth'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestData)
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle different error response formats
        let errorMessage = 'Failed to complete registration';
        if (typeof data === 'string') {
          errorMessage = data;
        } else if (data?.detail) {
          errorMessage = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
        } else if (data?.error) {
          errorMessage = typeof data.error === 'string' ? data.error : JSON.stringify(data.error);
        } else if (data?.message) {
          errorMessage = typeof data.message === 'string' ? data.message : JSON.stringify(data.message);
        }
        throw new Error(errorMessage);
      }

      // Update tokens and user data
      if (data.access_token) {
        localStorage.setItem('authToken', data.access_token);
      }
      if (data.refresh_token) {
        localStorage.setItem('refreshToken', data.refresh_token);
      }

      // Update user in localStorage
      const updatedUser: User = {
        id: data.user.id.toString(),
        email: data.user.email,
        name: data.user.full_name,
        role: data.user.role as 'tailor' | 'customer' | 'admin',
        phone: data.user.phone_number || undefined,
        tier: 'free',
        timezone: 'Asia/Kolkata',
        lastLogin: new Date().toISOString()
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));

      // Navigate to appropriate dashboard
      const destination = 
        data.user.role === 'customer' ? '/customer/dashboard' :
        data.user.role === 'tailor' ? '/business/dashboard' :
        '/customer/dashboard';
      
      // Reload to update auth context
      window.location.href = destination;
    } catch (err: any) {
      setError(err.message || 'Failed to complete registration. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <Navigation />
      <div className="flex items-center justify-center px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-2xl"
        >
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Complete Your Registration
              </h1>
              <p className="text-gray-600">
                Please select your account type and provide a few details to get started
              </p>
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3"
              >
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-800">{error}</p>
              </motion.div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Role Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-4">
                  I want to register as: *
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <motion.button
                    type="button"
                    onClick={() => handleInputChange('role', 'customer')}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`p-6 border-2 rounded-xl text-left transition-all ${
                      formData.role === 'customer'
                        ? 'border-blue-500 bg-blue-50 shadow-lg'
                        : 'border-gray-200 hover:border-gray-300 bg-white hover:shadow-md'
                    }`}
                  >
                    <div className="flex items-center space-x-4">
                      <div className={`p-3 rounded-lg ${
                        formData.role === 'customer' ? 'bg-blue-100' : 'bg-gray-100'
                      }`}>
                        <Users className={`w-8 h-8 ${
                          formData.role === 'customer' ? 'text-blue-600' : 'text-gray-600'
                        }`} />
                      </div>
                      <div className="flex-1">
                        <div className={`text-lg font-semibold mb-1 ${
                          formData.role === 'customer' ? 'text-blue-700' : 'text-gray-700'
                        }`}>
                          Customer
                        </div>
                        <div className="text-sm text-gray-600">Order custom garments</div>
                      </div>
                      {formData.role === 'customer' && (
                        <Check className="w-6 h-6 text-blue-500" />
                      )}
                    </div>
                  </motion.button>

                  <motion.button
                    type="button"
                    onClick={() => handleInputChange('role', 'tailor')}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`p-6 border-2 rounded-xl text-left transition-all ${
                      formData.role === 'tailor'
                        ? 'border-blue-500 bg-blue-50 shadow-lg'
                        : 'border-gray-200 hover:border-gray-300 bg-white hover:shadow-md'
                    }`}
                  >
                    <div className="flex items-center space-x-4">
                      <div className={`p-3 rounded-lg ${
                        formData.role === 'tailor' ? 'bg-blue-100' : 'bg-gray-100'
                      }`}>
                        <Scissors className={`w-8 h-8 ${
                          formData.role === 'tailor' ? 'text-blue-600' : 'text-gray-600'
                        }`} />
                      </div>
                      <div className="flex-1">
                        <div className={`text-lg font-semibold mb-1 ${
                          formData.role === 'tailor' ? 'text-blue-700' : 'text-gray-700'
                        }`}>
                          Tailor/Business
                        </div>
                        <div className="text-sm text-gray-600">Provide tailoring services</div>
                      </div>
                      {formData.role === 'tailor' && (
                        <Check className="w-6 h-6 text-blue-500" />
                      )}
                    </div>
                  </motion.button>
                </div>
                {fieldErrors.role && (
                  <p className="mt-2 text-sm text-red-600">{fieldErrors.role}</p>
                )}
              </div>

              {/* Full Name */}
              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="fullName"
                  value={formData.fullName}
                  onChange={(e) => handleInputChange('fullName', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    fieldErrors.fullName ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter your full name"
                />
                {fieldErrors.fullName && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.fullName}</p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <InternationalPhoneInput
                  value={formData.phone}
                  onChange={(value) => handleInputChange('phone', value)}
                  error={fieldErrors.phone}
                />
                {fieldErrors.phone && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.phone}</p>
                )}
              </div>

              {/* Business Name (for tailors) */}
              {formData.role === 'tailor' && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <label htmlFor="businessName" className="block text-sm font-medium text-gray-700 mb-2">
                    Business Name *
                  </label>
                  <input
                    type="text"
                    id="businessName"
                    value={formData.businessName}
                    onChange={(e) => handleInputChange('businessName', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      fieldErrors.businessName ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter your business name"
                  />
                  {fieldErrors.businessName && (
                    <p className="mt-1 text-sm text-red-600">{fieldErrors.businessName}</p>
                  )}
                </motion.div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Completing Registration...</span>
                  </>
                ) : (
                  <span>Complete Registration</span>
                )}
              </button>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default OAuthCompletionPage;

